import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import JunoPage from "../../components/JunoPage/JunoPage";
import HierarchyChartView from "../../components/HierarchyChartView/HierarchyChartView";
import HierarchyTreeView from "../../components/HierarchyTreeView/HierarchyTreeView";
import { HealthcareService } from "fhir/r5";
import { Loader, Title } from "@fyrstain/hl7-front-library";
import { Badge, Button, Card, Form, InputGroup } from "react-bootstrap";
import i18n from "i18next";
import HealthcareServiceService from "../../services/HealthcareServiceService";
import { useErrorNavigation } from "../../services/useErrorNavigation";
import {
  buildHealthcareServiceTree,
  countHealthcareServiceLeaves,
  countHealthcareServiceNodes,
  countHealthcareServicesByLevel,
  filterHealthcareServiceTree,
  getHealthcareServiceDisplayName,
  getHealthcareServiceKey,
  getHealthcareServiceSearchTerms,
  normalizeHealthcareServiceSearchValue,
} from "../../utils/healthcareServiceTree";
import "../../components/HierarchyView/HierarchyView.css";

const HealthcareServiceStructure: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //           State          //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [healthcareServices, setHealthcareServices] = useState<
    HealthcareService[]
  >([]);
  const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>(
    {}
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"tree" | "chart">("tree");

  const onError = useErrorNavigation();

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await HealthcareServiceService.loadAllInternalHealthcareServices();
      setHealthcareServices(response);
    } catch (error) {
      onError(error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  //////////////////////////////
  //     Tree data            //
  //////////////////////////////

  const tree = useMemo(
    () => buildHealthcareServiceTree(healthcareServices),
    [healthcareServices]
  );
  const normalizedSearchTerm = useMemo(
    () => normalizeHealthcareServiceSearchValue(searchTerm.trim()),
    [searchTerm]
  );
  const filteredTree = useMemo(
    () => filterHealthcareServiceTree(tree, normalizedSearchTerm),
    [tree, normalizedSearchTerm]
  );
  const totalHealthcareServices = healthcareServices.length;
  const totalRoots = filteredTree.nodes.length;
  const totalLeaves = countHealthcareServiceLeaves(filteredTree.nodes);
  const treeCount = countHealthcareServiceNodes(filteredTree.nodes);
  const healthcareServicesByLevel = useMemo(
    () => countHealthcareServicesByLevel(filteredTree.nodes),
    [filteredTree.nodes]
  );
  const hasSearch = normalizedSearchTerm.length > 0;
  const visibleMatches = hasSearch ? filteredTree.matches : totalHealthcareServices;
  const isChartView = viewMode === "chart";

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const toggleNode = useCallback((key: string) => {
    setCollapsedNodes((current) => ({
      ...current,
      [key]: !(current[key] ?? true),
    }));
  }, []);

  const goToHealthcareServiceDetails = useCallback(
    (healthcareServiceId?: string | null) => {
      if (healthcareServiceId) {
        navigate(`/HealthcareService/${encodeURIComponent(healthcareServiceId)}`);
      }
    },
    [navigate]
  );

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey={i18n.t("title.healthcareservicestructure")}
      loading={loading && !isChartView}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header>
            <Title
              level={2}
              prefix="Structure"
              content={i18n.t("title.healthcareservicestructure")}
            />
          </Card.Header>
          <Card.Body>
            <div className="hierarchy-view-switch">
              <Button
                variant={isChartView ? "outline-secondary" : "secondary"}
                onClick={() => setViewMode("tree")}
              >
                {i18n.t("label.treeview")}
              </Button>
              <Button
                variant={isChartView ? "secondary" : "outline-secondary"}
                onClick={() => setViewMode("chart")}
              >
                {i18n.t("label.chartview")}
              </Button>
            </div>

            {isChartView ? (
              <HierarchyChartView<HealthcareService>
                items={healthcareServices}
                loading={loading}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                buildTree={buildHealthcareServiceTree}
                getKey={getHealthcareServiceKey}
                getDisplayName={getHealthcareServiceDisplayName}
                getSearchTerms={getHealthcareServiceSearchTerms}
                onGoToDetails={goToHealthcareServiceDetails}
                labels={{
                  searchPlaceholder: i18n.t("label.healthcareservicesearch"),
                  reset: i18n.t("button.reset"),
                  center: i18n.t("button.centerview"),
                  expandAll: i18n.t("button.expandall"),
                  collapseAll: i18n.t("button.collapseall"),
                  root: i18n.t("label.root"),
                  level: i18n.t("label.level"),
                  childItems: i18n.t("label.healthcareservices"),
                  leaf: i18n.t("label.leaf"),
                  searchResults: i18n.t("label.searchresults"),
                  structureEmpty: i18n.t("title.healthcareservicestructureempty"),
                  structureNoResults: i18n.t("title.healthcareservicestructurenoresults"),
                  open: i18n.t("label.openhealthcareservice"),
                  close: i18n.t("label.closehealthcareservice"),
                  details: i18n.t("label.details"),
                  totalItemsLabel: i18n.t("label.healthcareservices"),
                  rootItemsLabel: i18n.t("label.root"),
                  leafItemsLabel: i18n.t("label.leaf"),
                }}
              />
            ) : (
              <>
                <div className="hierarchy-search">
                  <InputGroup>
                    <Form.Control
                      type="search"
                      value={searchTerm}
                      placeholder={i18n.t("label.healthcareservicesearch")}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearchTerm("")}
                      disabled={!searchTerm}
                    >
                      {i18n.t("button.reset")}
                    </Button>
                  </InputGroup>
                </div>
                <div className="d-flex flex-wrap gap-3 mb-4">
                  <Badge bg="secondary">
                    {totalHealthcareServices} {i18n.t("label.healthcareservices")}
                  </Badge>
                  <Badge bg="info">
                    {totalRoots} {i18n.t("label.root")}
                  </Badge>
                  <Badge bg="success">
                    {totalLeaves} {i18n.t("label.leaf")}
                  </Badge>
                  {hasSearch && (
                    <Badge bg="primary">
                      {visibleMatches} {i18n.t("label.searchresults")}
                    </Badge>
                  )}
                  {healthcareServicesByLevel.map((count, index) => (
                    <Badge
                      key={`level-${index}`}
                      bg={index % 2 === 0 ? "dark" : "secondary"}
                    >
                      {i18n.t("label.level")} {index + 1}: {count}
                    </Badge>
                  ))}
                </div>
                {loading ? (
                  <div className="displayFlexCenter py-5">
                    <Loader />
                  </div>
                ) : treeCount > 0 ? (
                  <HierarchyTreeView<HealthcareService>
                    filteredTree={filteredTree}
                    collapsedNodes={collapsedNodes}
                    normalizedSearchTerm={normalizedSearchTerm}
                    onToggleNode={toggleNode}
                    onGoToDetails={goToHealthcareServiceDetails}
                    getKey={getHealthcareServiceKey}
                    getDisplayName={getHealthcareServiceDisplayName}
                    labels={{
                      open: i18n.t("label.openhealthcareservice"),
                      close: i18n.t("label.closehealthcareservice"),
                      details: i18n.t("label.details"),
                      root: i18n.t("label.root"),
                      level: i18n.t("label.level"),
                      subItems: i18n.t("label.healthcareservices"),
                      leaf: i18n.t("label.leaf"),
                      identifier: i18n.t("label.identifier"),
                      parentIdentifier: i18n.t("label.offeredin"),
                      rootValue: "ROOT",
                    }}
                  />
                ) : (
                  <div>
                    {hasSearch
                      ? i18n.t("title.healthcareservicestructurenoresults")
                      : i18n.t("title.healthcareservicestructureempty")}
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default HealthcareServiceStructure;
